import type {
  BootstrapResponse,
  ChangeMarker,
  CheckLabel,
  CommandObservation,
  HumanReview,
  ImportState,
  SessionListItem,
} from "../contracts";

let csrf = "";
export async function getCollectionHealth():Promise<import('../collection-health').CollectionHealth|null> {
 const res=await fetch('/api/collection');
 if(res.status===404)return null; // Compatible with older local backends.
 if(!res.ok)throw new Error('Collection status unavailable');
 return res.json();
}

export type OverviewSnapshot = {
  id: string;
  commitSha: string | null;
  commitTime?: string | null;
  collectedAt: string;
  sourceKind: string;
  profile: string;
  status: string;
  aggregates: Record<string, number | null>;
  metricStates: Record<string, string>;
  reviewThresholds?: { cyclomatic: number; cognitive: number; functionSpan?: number };
};

export type OverviewBaseline = {
  id: string;
  commitSha: string | null;
  commitTime?: string | null;
  collectedAt: string;
  profile: string;
  status: string;
};

export type OverviewComparison = {
  metrics?: { key: string; before: number | null; after: number | null; absolute: number | null; comparable: boolean; reason?: string | null }[];
  fileChanges?: { path: string; delta: number | null; status?: string }[];
  complexityChanges?: { path: string; name: string; line: number | null; cyclomatic: { before: number | null; after: number | null }; cognitive: { before: number | null; after: number | null } }[];
  findings?: { added?: { path?: string; name?: string; rule?: string; category?: string; source?: string }[] };
  methodWarning?: string | null;
};

export type BudgetViolationView = {
  id: string; snapshotId: string; baselineId: string; commitSha: string | null;
  total: number; maxNewCrossings: number; gap?: number | null;
  crossings: { path: string; name: string; line: number | null }[];
  createdAt: string;
};
export type HealthBudgetView = { enabled: boolean; maxNewCrossings: number; updatedAt: string | null };
export type OverviewPayload = {
  budget?: HealthBudgetView;
  violations?: BudgetViolationView[];
  latest: OverviewSnapshot | null;
  baseline: OverviewBaseline | null;
  baselineCandidates: OverviewBaseline[];
  changes: OverviewComparison | null;
  hotspots: Record<string, Record<string, unknown>[]> | null;
  snapshots: Record<string, unknown>[];
  runs: { adapter: string; status: string; duration_ms: number; warnings_json?: string; aggregates_json?: string }[];
  events: { t: string; label: string; kind: string; status?: string }[];
  decision: { coverage: string; sourceKind: string; baselineId: string | null } | null;
};

export function setCsrf(token: string): void {
  csrf = token;
}

export async function req(path: string, init: RequestInit = {}): Promise<Response> {
  const headers = new Headers(init.headers);
  if (init.method && init.method !== "GET") {
    headers.set("x-csrf-token", csrf);
    if (!headers.has("content-type") && init.body) headers.set("content-type", "application/json");
  }
  return fetch(path, { ...init, headers });
}

export async function getBootstrap(): Promise<BootstrapResponse> {
  const res = await fetch("/api/bootstrap");
  if (!res.ok) throw new Error("Failed to load bootstrap");
  return res.json();
}

export async function getSessions(signal?: AbortSignal): Promise<SessionListItem[]> {
  const res = await fetch("/api/sessions", {signal});
  if (!res.ok) throw new Error("Failed to load sessions");
  const body = (await res.json()) as { sessions: SessionListItem[] };
  return body.sessions;
}

export async function getSessionDetail(id: string, cursor = 0): Promise<{
  session: SessionListItem;
  review: HumanReview | null;
  checkLabels: CheckLabel[];
  commands: CommandObservation[];
  commandCursor: number | null;
  totalCommands: number;
  verification: {feedback:import('../verification/feedback').FeedbackMetric[];tasks?:import('../verification/feedback').FeedbackTask[];flakyChecks:Array<{commandId:string;name:string}>};
}> {
  const res = await fetch(`/api/sessions/${encodeURIComponent(id)}?cursor=${cursor}`);
  if (!res.ok) throw new Error("Failed to load session");
  return res.json();
}

export async function getMarkers(): Promise<ChangeMarker[]> {
  const res = await fetch("/api/markers");
  if (!res.ok) throw new Error("Failed to load markers");
  const body = (await res.json()) as { markers: ChangeMarker[] };
  return body.markers;
}

export async function getImport(): Promise<ImportState> {
  const res = await fetch("/api/import");
  return res.json();
}

export async function postImport(force: boolean): Promise<Response> {
  return req("/api/import", { method: "POST", body: JSON.stringify({ force }) });
}

export async function saveMarker(input: unknown, id?: string): Promise<Response> {
  return req(id ? `/api/markers/${id}` : "/api/markers", {
    method: id ? "PATCH" : "POST",
    body: JSON.stringify(input),
  });
}

export async function removeMarker(id: string): Promise<Response> {
  return req(`/api/markers/${id}`, { method: "DELETE" });
}

export async function saveReview(sessionId: string, body: unknown): Promise<Response> {
  return req(`/api/sessions/${sessionId}/review`, { method: "PUT", body: JSON.stringify(body) });
}

export async function clearReview(sessionId: string): Promise<Response> {
  return req(`/api/sessions/${sessionId}/review`, { method: "DELETE" });
}

export async function addCheckLabel(sessionId: string, body: unknown): Promise<Response> {
  return req(`/api/sessions/${sessionId}/check-labels`, { method: "POST", body: JSON.stringify(body) });
}

export async function removeCheckLabel(id: string): Promise<Response> {
  return req(`/api/check-labels/${id}`, { method: "DELETE" });
}

export async function healthTools(signal?: AbortSignal) {
  return readJson(await fetch("/api/health/tools", {signal}), "Local settings unavailable");
}
export async function healthSetup(body: unknown) {
  return req("/api/health/setup", { method: "POST", body: JSON.stringify(body) });
}
export async function saveHealthInvestigation(body: unknown) {
  return req("/api/health/investigation", { method: "POST", body: JSON.stringify(body) });
}
export async function healthInvestigation() {
  return (await fetch("/api/health/investigation")).json();
}
export async function healthFlow() {
  return (await fetch("/api/health/flow")).json();
}
export async function healthAdoption(pkg: string) {
  return (await fetch(`/api/health/adoption?package=${encodeURIComponent(pkg)}`)).json();
}
export async function healthScan(body: unknown) {
  return req("/api/health/scan", { method: "POST", body: JSON.stringify(body) });
}
export async function healthBudget(): Promise<{ budget: HealthBudgetView; violations: BudgetViolationView[] }> {
  return (await fetch("/api/health/budget")).json();
}
export async function saveHealthBudget(body: unknown) {
  return req("/api/health/budget", { method: "POST", body: JSON.stringify(body) });
}
export async function checkHealthBudget(snapshotId: string) {
  return req("/api/health/budget/check", { method: "POST", body: JSON.stringify({ snapshotId }) }).then((r) => r.json());
}
export async function healthOverview(signal?: AbortSignal): Promise<OverviewPayload> {
  return readJson(await fetch("/api/health/overview", {signal}), "Code measurements unavailable");
}
export async function healthFiles(snapshot: string, extra = "") {
  return readJson(await fetch(`/api/health/files?snapshot=${encodeURIComponent(snapshot)}${extra}`), "Code measurements unavailable");
}
export async function healthFunctions(snapshot: string, extra = "") {
  return readJson(await fetch(`/api/health/functions?snapshot=${encodeURIComponent(snapshot)}${extra}`), "Code measurements unavailable");
}
export async function healthFindings(snapshot: string, extra = "") {
  return readJson(await fetch(`/api/health/findings?snapshot=${encodeURIComponent(snapshot)}${extra}`), "Code measurements unavailable");
}
export async function healthDirectories(snapshot: string, extra = "") {
  return readJson(await fetch(`/api/health/directories?snapshot=${encodeURIComponent(snapshot)}${extra}`), "Code measurements unavailable");
}
export async function healthFileHistory(snapshot: string, path: string) { return (await fetch(`/api/health/file-history?snapshot=${encodeURIComponent(snapshot)}&path=${encodeURIComponent(path)}`)).json(); }
export async function healthFileTrend(snapshot: string, path: string) { return (await fetch(`/api/health/file-trend?snapshot=${encodeURIComponent(snapshot)}&path=${encodeURIComponent(path)}`)).json(); }
export async function healthDependencies(snapshot: string, path: string, offset = 0) { return (await fetch(`/api/health/dependencies?snapshot=${encodeURIComponent(snapshot)}&path=${encodeURIComponent(path)}&limit=50&offset=${offset}`)).json(); }
export async function healthChanges(signal?: AbortSignal) { const response=await fetch("/api/health/changes",{signal:signal??AbortSignal.timeout(15000)});const body=await response.json();if(!response.ok)throw Error(body.message??body.error??"Changes unavailable");if(!Array.isArray(body.changes))throw Error("Changes response is incomplete");return body; }
export async function healthChange(id: string, selection: { before?: string; after?: string } = {}) {
  const query = new URLSearchParams(Object.entries(selection).filter(([, value]) => Boolean(value)) as [string, string][]);
  const res = await fetch(`/api/health/changes/${encodeURIComponent(id)}${query.size ? `?${query}` : ""}`,{signal:AbortSignal.timeout(15000)});
  const body = await res.json();
  if (!res.ok) throw new Error(body.message ?? body.error ?? "Failed to load change evidence");
  return body;
}
export async function healthChangePacket(id: string, selection: { before?: string; after?: string } = {}) {
  const query = new URLSearchParams(Object.entries(selection).filter(([, value]) => Boolean(value)) as [string, string][]);
  const res = await fetch(`/api/health/changes/${encodeURIComponent(id)}/packet${query.size ? `?${query}` : ""}`);
  const body = await res.json();
  if (!res.ok) throw new Error(body.message ?? body.error ?? "Failed to create change packet");
  return body as { markdown: string; json: Record<string, unknown> };
}
export async function createHealthChange(body: unknown) { return req("/api/health/changes", { method: "POST", body: JSON.stringify(body) }); }
export async function updateHealthChange(id: string, body: unknown) { return req(`/api/health/changes/${encodeURIComponent(id)}`, { method: "PATCH", body: JSON.stringify(body) }); }
export async function healthCrossings(before: string, after: string) {
  const query = new URLSearchParams({ before, after });
  const res = await fetch(`/api/health/crossings?${query}`);
  const body = await res.json();
  if (!res.ok) throw new Error(body.message ?? body.error ?? "Failed to load threshold crossings");
  return body as {
    crossings: { path: string; name: string; line: number | null; appeared: boolean; cc: { before: number | null; after: number; threshold: number } | null; cognitive: { before: number | null; after: number; threshold: number } | null; span: { before: number | null; after: number; threshold: number } | null }[];
    total: number;
    comparable: boolean;
    reason: string | null;
    thresholds: { cyclomatic: number; cognitive: number; functionSpan: number };
  };
}
export async function healthCompare(a: string, b: string, options: Record<string, unknown> = {}) {
  const query = new URLSearchParams(Object.entries({ a, b, ...options }).map(([key, value]) => [key, String(value)]));
  return readJson(await req(`/api/health/compare?${query}`), "Could not compare these revisions");
}

export async function readJson(response: Response, fallback: string) {
  const body = await response.json().catch(() => null);
  if (!response.ok) {
    const detail = body?.message ?? fallback;
    const reason = response.status === 403 ? "Access denied" : response.status >= 500 ? "Service error" : "Request failed";
    throw new Error(`${reason} (${response.status}) · ${detail}`);
  }
  if (body === null || body.error) throw new Error(body?.message ?? fallback);
  return body;
}
export async function healthHistoryPreview(options: Record<string, string | number> = {}) {
  const query = new URLSearchParams(Object.entries(options).map(([k, v]) => [k, String(v)]));
  return (await fetch(`/api/health/history?${query}`)).json();
}
export async function healthHistoryStart(shas?: string[], profile:"structural"|"full"="structural", prepareDependencies=false) {
  return req("/api/health/history", { method: "POST", body: JSON.stringify({ shas, profile, prepareDependencies }) });
}
export async function healthPacket(snapshotId: string, path?: string, scope: Record<string, string | undefined> = {}) {
  return req("/api/health/packet", { method: "POST", body: JSON.stringify({ snapshotId, path, ...scope }) }).then((r) => r.json());
}
export async function healthChecks() {
  return readJson(await fetch("/api/health/checks"), "Local settings unavailable");
}
export async function healthCheckRuns() { return readJson(await fetch("/api/health/check-runs?limit=50"), "Local settings unavailable"); }
export async function runHealthCheck(id: string) {
  return req(`/api/health/checks/${id}/run`, { method: "POST", body: JSON.stringify({}) });
}
export async function approveHealthCheck(id: string, approved: boolean) {
  return req(`/api/health/checks/${id}/approve`, { method: "POST", body: JSON.stringify({ approved }) });
}
export async function healthSnapshots(includeHidden = false) {
  return readJson(await fetch(`/api/health/snapshots${includeHidden ? "?includeHidden=true" : ""}`), "Code measurements unavailable");
}
export async function setHealthSnapshotVisibility(id: string, hidden: boolean, reason?: string) {
  return req(`/api/health/snapshots/${encodeURIComponent(id)}/visibility`, { method: "POST", body: JSON.stringify({ hidden, reason: reason ?? null }) });
}
export async function healthJobs() {
  return readJson(await fetch("/api/health/jobs"), "Local settings unavailable");
}
export async function healthStorage() { return readJson(await fetch("/api/health/storage"), "Local settings unavailable"); }
export async function cancelHealthJob(id: string, signal?: AbortSignal) {
  return req(`/api/health/jobs/${encodeURIComponent(id)}/cancel`, { method: "POST", body: "{}", signal });
}
export async function retryHealthJob(id: string, signal?: AbortSignal) {
  return req(`/api/health/jobs/${encodeURIComponent(id)}/retry`, { method: "POST", body: "{}", signal });
}

// Versioned receipts are deliberately a separate input from native Codex activity.
// They are producer-asserted evidence, never a request to run another agent.
export async function importExternalEvidence(payload: unknown) {
  return req("/api/health/evidence/import", { method: "POST", body: JSON.stringify(payload) });
}
export async function importCodexExecEvidence(text: string, producer?: { name: string; version?: string }) {
  return req("/api/health/evidence/exec", { method: "POST", body: JSON.stringify({ text, producer }) });
}
export async function healthEvidenceRuns(query = "") { return (await fetch(`/api/health/evidence/runs${query}`)).json(); }
export async function healthEvidenceChecks(query = "") { return (await fetch(`/api/health/evidence/checks${query}`)).json(); }
export async function healthEvidenceCohorts(query = "") { return (await fetch(`/api/health/evidence/cohorts${query}`)).json(); }
export async function healthOutcomes(query = "") { return (await fetch(`/api/health/outcomes${query}`)).json(); }
export async function healthOutcomeWork(id: string) { return (await fetch(`/api/health/outcomes/work/${encodeURIComponent(id)}`)).json(); }
export async function importCoverageEvidence(body: unknown) { return req("/api/health/outcomes/coverage", { method: "POST", body: JSON.stringify(body) }); }
export async function importGithubEvidence(body: unknown) { return req("/api/health/outcomes/github/import", { method: "POST", body: JSON.stringify(body) }); }
export async function saveOutcomeAllocation(body: unknown) { return req("/api/health/outcomes/allocations", { method: "POST", body: JSON.stringify(body) }); }
export async function saveOutcomeAcceptance(body: unknown) { return req("/api/health/outcomes/acceptances", { method: "POST", body: JSON.stringify(body) }); }

export async function saveLedgerOwnerState(sessionId:string,kind:"assessments"|"acceptance"|"associations",body:unknown):Promise<void> {
 const response=await req(`/api/health/ledger/${encodeURIComponent(sessionId)}/${kind}`,{method:"POST",body:JSON.stringify(body)});
 if(!response.ok)throw new Error(response.status===409?"Evidence changed or revision conflicts; reload this session before saving.":"Could not save the owner record.");
}

export function ledgerMaintenanceAction(action:"start"|"step"|"publish"|"cancel"){return req("/api/health/ledger-maintenance",{method:"POST",body:JSON.stringify({action,optIn:action==="start"})});}
